<template>
  <span class="sales-basis-info">
    <button type="button" class="basis-chip" data-testid="sales-basis-trigger" @click="dialogVisible = true">
      <i class="el-icon-info"></i>
      <span>{{ chipLabel }}</span>
    </button>

    <el-dialog
      :visible.sync="dialogVisible"
      :title="$t('매출 금액 기준 안내')"
      :width="$device.mobile ? '94%' : '680px'"
      custom-class="sales-basis-dialog"
      append-to-body>
      <p class="intro">
        {{
          $t(
            '오더엔은 화면의 목적에 따라 서로 다른 매출 기준을 사용합니다. 같은 주문이라도 화면에 따라 금액이 다르게 보일 수 있으며, 이는 오류가 아닙니다.'
          )
        }}
      </p>

      <div class="basis-cards">
        <div
          v-for="item in basisCards"
          :key="item.key"
          :class="['basis-card', { active: item.key === basis }]"
          :data-testid="`sales-basis-card-${item.key}`">
          <div class="basis-card-head">
            <span class="basis-dot" :style="{ backgroundColor: item.color }"></span>
            <span class="basis-name">{{ item.name }}</span>
            <span v-if="item.key === basis" class="basis-current">{{ $t('현재 화면') }}</span>
          </div>
          <p class="basis-desc">{{ item.desc }}</p>
          <p class="basis-where"><i class="el-icon-monitor"></i> {{ item.where }}</p>
        </div>
      </div>

      <div class="example">
        <div class="example-title">{{ $t('예시로 이해하기 — 쿠팡이츠 주문 1건') }}</div>
        <div class="example-grid">
          <div class="example-flow">
            <div class="flow-caption">{{ $t('고객이 결제할 때') }}</div>
            <div class="flow-row">
              <span class="flow-label">{{ $t('기준매출 (주문 금액)') }}</span>
              <span class="flow-amount">18,000{{ $t('원') }}</span>
            </div>
            <div class="flow-row minus">
              <span class="flow-label">{{ $t('앱 할인 (상점쿠폰 3,000 + 즉시할인 1,000)') }}</span>
              <span class="flow-amount">- 4,000{{ $t('원') }}</span>
            </div>
            <div class="flow-row result">
              <span class="flow-label">{{ $t('실결제') }}</span>
              <span class="flow-amount">14,000{{ $t('원') }}</span>
            </div>
          </div>
          <div class="example-flow">
            <div class="flow-caption">{{ $t('배달앱이 정산할 때') }}</div>
            <div class="flow-row">
              <span class="flow-label">{{ $t('기준매출 (주문 금액)') }}</span>
              <span class="flow-amount">18,000{{ $t('원') }}</span>
            </div>
            <div class="flow-row minus">
              <span class="flow-label">{{ $t('정산 차감 (중개수수료·배달비·할인 등)') }}</span>
              <span class="flow-amount">- 11,396{{ $t('원') }}</span>
            </div>
            <div class="flow-row result highlight">
              <span class="flow-label">{{ $t('정산 예상액') }}</span>
              <span class="flow-amount">6,604{{ $t('원') }}</span>
            </div>
          </div>
        </div>
      </div>

      <ul class="notes">
        <li>
          {{
            $t(
              '통계 화면(대시보드·매출/시간별 현황)의 매출은 실결제 기준입니다. 배달 매출 관리의 기준매출과는 앱 할인만큼 차이가 납니다.'
            )
          }}
        </li>
        <li>
          {{
            $t(
              '배달앱 매출에 함께 표시되는 "정산 기준매출"은 정산 데이터가 수집된 주문만 합산한 참고 값입니다. 정산 수집 전에는 0원일 수 있고, 수집되면 채워집니다.'
            )
          }}
        </li>
        <li>{{ $t('상품별 매출은 메뉴 정가 × 수량 기준이라 할인이 반영되지 않습니다.') }}</li>
        <li>
          {{
            $t(
              '가게배달 팁·요기요 배달요금처럼 가게가 받는 금액은 정산 예상에 가산(가게 수취)으로 표시됩니다. 후불(만나서결제) 주문의 정산 예상은 현장에서 받은 금액을 포함한 값이라, 배달앱의 입금예정 금액(수수료 회수로 음수 가능)과는 다릅니다.'
            )
          }}
        </li>
      </ul>

      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">{{ $t('닫기') }}</el-button>
      </span>
    </el-dialog>
  </span>
</template>

<script>
export default {
  name: 'SalesBasisInfo',
  props: {
    // 현재 화면이 쓰는 매출 기준: net(실결제) | gross(정산 기준매출) | list(정가)
    basis: {
      type: String,
      default: 'net',
    },
  },
  data() {
    return {
      dialogVisible: false,
    }
  },
  computed: {
    chipLabel() {
      const labels = {
        net: this.$t('실결제 기준'),
        gross: this.$t('기준매출(정산) 기준'),
        list: this.$t('정가 기준'),
      }
      return labels[this.basis] || labels.net
    },
    basisCards() {
      return [
        {
          key: 'net',
          color: '#5B8FF9',
          name: this.$t('실결제'),
          desc: this.$t('고객이 실제로 결제한 금액입니다. 배달앱 할인(상점쿠폰·즉시할인)이 빠진 후의 금액입니다.'),
          where: this.$t('대시보드 · 매출 현황 · 시간별 매출'),
        },
        {
          key: 'gross',
          color: '#F27438',
          name: this.$t('기준매출'),
          desc: this.$t('배달앱이 정산의 기준으로 삼는 주문 금액입니다. 할인이 빠지기 전의 금액입니다.'),
          where: this.$t('배달 매출 관리'),
        },
        {
          key: 'list',
          color: '#61C0A8',
          name: this.$t('정가'),
          desc: this.$t('메뉴판 가격 × 수량입니다. 할인이 반영되지 않은 판매액입니다.'),
          where: this.$t('상품별 매출 현황'),
        },
      ]
    },
  },
}
</script>

<style scoped>
.basis-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: 1px solid var(--color-border);
  border-radius: 999px;
  background: var(--color-bg-card);
  color: var(--color-text-regular);
  font-size: 12px;
  line-height: 1.5;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}
.basis-chip:hover {
  border-color: var(--color-primary);
  color: var(--color-primary);
}
.basis-chip i {
  color: var(--color-primary);
}

.intro {
  margin: 0 0 14px;
  color: var(--color-text-regular);
  font-size: 13px;
  line-height: 1.6;
}

.basis-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}
.basis-card {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 10px 12px;
  background: var(--color-bg-card);
}
.basis-card.active {
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary) inset;
}
.basis-card-head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.basis-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
}
.basis-name {
  font-weight: 700;
  font-size: 13px;
  color: var(--color-text-primary);
}
.basis-current {
  margin-left: auto;
  padding: 1px 6px;
  border-radius: 999px;
  background: var(--color-primary);
  color: #fff;
  font-size: 10px;
  flex: none;
}
.basis-desc {
  margin: 8px 0 6px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--color-text-regular);
  word-break: keep-all;
}
.basis-where {
  margin: 0;
  font-size: 11px;
  color: var(--color-text-secondary);
}
.basis-where i {
  margin-right: 2px;
}

.example {
  margin-top: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 12px;
  background: #fdf8f4;
}
.example-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: 10px;
}
.example-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.example-flow {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 10px;
}
.flow-caption {
  font-size: 11px;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
}
.flow-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  padding: 3px 0;
  font-size: 12px;
  color: var(--color-text-regular);
}
.flow-row.minus .flow-amount {
  color: #d9534f;
}
.flow-row.result {
  border-top: 1px dashed var(--color-border);
  margin-top: 4px;
  padding-top: 7px;
  font-weight: 700;
  color: var(--color-text-primary);
}
.flow-row.result.highlight .flow-amount {
  color: var(--color-primary-dark);
}
.flow-label {
  word-break: keep-all;
}
.flow-amount {
  flex: none;
  font-variant-numeric: tabular-nums;
}

.notes {
  margin: 14px 0 0;
  padding-left: 18px;
}
.notes li {
  font-size: 12px;
  line-height: 1.7;
  color: var(--color-text-regular);
  word-break: keep-all;
}

@media (max-width: 768px) {
  .basis-cards {
    grid-template-columns: 1fr;
  }
  .example-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.sales-basis-dialog {
  border-radius: 12px;
}
.sales-basis-dialog .el-dialog__body {
  padding-top: 12px;
}
</style>
